import { Alert, AlertTitle, AlertDescription, Button, Text, Separator } from '@flatfile/baseline'
import { UseMutateFunction } from '@tanstack/react-query'
import { BillingSubscriptionItem, Product } from '../../../api/types'
import { formatCurrency, formatNumber } from '../utilities/billingHelpers'
import { getPlanChange } from '../utilities/stripe'
import { Emph } from './BillingComponents'

const ModalErrorBanner = () => (
  <Alert variant='outline'>
    <AlertTitle>An Error occurred</AlertTitle>
    <AlertDescription>
      <div>Try refreshing the page</div>
      <Separator className='my-4' />
      <div>
        If you're concerned about incorrect billing, or you made a purchase you didn't intend to, please reach out to{' '}
        <a href='mailto:support@flatfile.com' className='text-primary hover:underline'>
          support@flatfile.com
        </a>
      </div>
    </AlertDescription>
  </Alert>
)

export const UpdateSubscriptionModalContent = ({
  subscriptionItems,
  starterProduct,
  productsData,
  selectedPlanId,
  selectedFileUsageId,
  selectedPDVUsageId,
  updateSubscription,
  subscriptionUpdateIsError,
  subscriptionUpdateIsLoading,
  hideUpdateSubscriptionModal,
}: {
  subscriptionItems: BillingSubscriptionItem[]
  starterProduct: Product | undefined
  productsData: Product[]
  selectedPlanId: string | undefined
  selectedFileUsageId: string | undefined
  selectedPDVUsageId: string | undefined
  updateSubscription: UseMutateFunction<unknown, unknown, void, unknown>
  subscriptionUpdateIsError: boolean
  subscriptionUpdateIsLoading: boolean
  hideUpdateSubscriptionModal: () => void
}) => {
  const {
    currentPlanText,
    currentPlanPrice,
    currentFileCommitmentText,
    currentPDVCommitmentText,
    newPlanText,
    newPlanPrice,
    newFileCommitmentText,
    newFileCommitmentPrice,
    newPDVCommitmentText,
    newPDVCommitmentPrice,
    newFileCommitmentPrepayPrice,
    newFileCommitmentUpTo,
    newPDVCommitmentPrepayPrice,
    newPDVCommitmentUpTo,
  } = getPlanChange({
    subscriptionItems,
    starterProduct,
    productsData,
    selectedPlanId,
    selectedFileUsageId,
    selectedPDVUsageId,
    freeFileAllowance: 50,
    freePDVAllowance: 10,
  })

  return (
    <>
      {subscriptionUpdateIsError && <ModalErrorBanner />}

      <Text variant='title2' emphasized className='mb-4'>
        Update billing plan
      </Text>
      <div className='mt-4'>
        Changes:
        <ul className='list-disc pl-5 mt-2 space-y-2'>
          {currentPlanText !== newPlanText && (
            <li>
              <div>
                Plan changed from <Emph>{currentPlanText}</Emph> to <Emph>{newPlanText}</Emph>
              </div>
              {formatCurrency(currentPlanPrice)} →{formatCurrency(newPlanPrice!)}
            </li>
          )}
          {currentFileCommitmentText !== newFileCommitmentText && (
            <li>
              <div>
                File commitment changed from <Emph>{currentFileCommitmentText}</Emph> to{' '}
                <Emph>{newFileCommitmentText}</Emph> at {formatCurrency(newFileCommitmentPrepayPrice)} per month.
              </div>
              Including your free allowance your plan now includes {formatNumber(newFileCommitmentUpTo)} files per
              month. Any additional files will be charged at {formatCurrency(newFileCommitmentPrice)} per file.
            </li>
          )}
          {currentPDVCommitmentText !== newPDVCommitmentText && (
            <li>
              <div>
                PDV commitment changed from <Emph>{currentPDVCommitmentText}</Emph> {'to '}{' '}
                <Emph>{newPDVCommitmentText}</Emph> at {formatCurrency(newPDVCommitmentPrepayPrice)} per month.
              </div>
              Including your free allowance your plan now includes {formatNumber(newPDVCommitmentUpTo)} Million PDV per
              month. Any additional PDV will be charged at {formatCurrency(newPDVCommitmentPrice)} per million PDV
            </li>
          )}
        </ul>
      </div>
      <div className='flex min-h-[50px] gap-4 justify-end items-center flex-row flex-wrap mt-6'>
        <Button variant='outline' disabled={subscriptionUpdateIsLoading} onClick={() => hideUpdateSubscriptionModal()}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            updateSubscription()
          }}
          loading={subscriptionUpdateIsLoading}
        >
          Confirm
        </Button>
      </div>
    </>
  )
}

export const CancelSubscriptionModalContent = ({
  cancelSubscription,
  hideCancelSubscriptionModal,
  subscriptionCancellationIsError,
  subscriptionCancellationIsLoading,
}: {
  cancelSubscription: UseMutateFunction<unknown, unknown, void, unknown>
  hideCancelSubscriptionModal: () => void
  subscriptionCancellationIsError: boolean
  subscriptionCancellationIsLoading: boolean
}) => {
  return (
    <>
      {subscriptionCancellationIsError && <ModalErrorBanner />}
      <Text variant='title1' emphasized className='mb-4'>
        Cancel Subscription
      </Text>
      <Text className='mt-4'>
        Are you sure you want to cancel your subscription? If you cancel, your account will be moved to the free Starter
        plan with a monthly usage limit.
      </Text>

      <div className='flex min-h-[50px] gap-4 justify-end items-center flex-row flex-wrap mt-6'>
        <Button
          variant='outline'
          disabled={subscriptionCancellationIsLoading}
          onClick={() => hideCancelSubscriptionModal()}
        >
          Keep my current plan
        </Button>
        <Button
          onClick={() => {
            cancelSubscription()
          }}
          loading={subscriptionCancellationIsLoading}
        >
          Cancel my subscription
        </Button>
      </div>
    </>
  )
}
export const ConfirmProAnnualModalContent = ({
  onClose,
  onConfirmCallback,
}: {
  onClose: () => void
  onConfirmCallback: (() => void) | null
}) => (
  <>
    <Text variant='title2' emphasized className='mb-4'>
      You're almost a Pro Annual Member!
    </Text>
    <div className='mt-4'>
      Before we proceed, here's a quick heads-up:
      <ul className='list-disc pl-5 mt-2 space-y-2'>
        <li>
          After completing checkout on the next screen, you'll be enrolled in our <Emph>Pro Annual plan.</Emph>
        </li>
        <li>You'll be billed at the discounted annual rate on a monthly basis.</li>
        <li>
          Once enrolled, you'll need to <Emph>contact us</Emph> to modify or cancel your plan.
        </li>
      </ul>
    </div>
    <div className='flex min-h-[50px] gap-4 justify-end items-center flex-row flex-wrap mt-6'>
      <Button variant='outline' onClick={onClose}>
        Cancel
      </Button>
      <Button
        onClick={() => {
          onConfirmCallback && onConfirmCallback()
          onClose()
        }}
      >
        Confirm
      </Button>
    </div>
  </>
)
